import { Grid, useTheme } from "@mui/material";
import { ButtonBar, DaButton, ISignatureDialogTextProps, SignatureDialog, SignatureSourceType, SignatureTerms } from "../_shared";
import React, { useState } from "react";
import { IPafApplication, IPafApplicationSignature } from "../../_api";
import { UseFormGetValues, UseFormResetField, UseFormSetValue } from "react-hook-form";

export type EsignSelectionProps = {
    getValues: UseFormGetValues<IPafApplication>;
    setValue: UseFormSetValue<IPafApplication>
    resetField: UseFormResetField<IPafApplication>
    source: SignatureSourceType;
}

export const EsignSelection = (
    {
        getValues, setValue, resetField, source
    }: EsignSelectionProps
) => {
    const [signatureDialogProps, setSignatureDialogProps] = useState<ISignatureDialogTextProps | undefined>(undefined);
    const [signatureDialogOpen, setSignatureDialogOpen] = React.useState<boolean>(false);
    const theme = useTheme();
    const signatureDialog_Show = (source: SignatureSourceType) => {
        const isNcoa = getValues("is_ncoa");
        const isNcoaWaiver = getValues("is_waiver_on_file");
        const isDsf2 = getValues("is_dsf");

        let includedTerms: Array<React.ReactElement> = [];
        if (isNcoa)
            includedTerms.push(SignatureTerms.ncoaLinkTerms);
        if (isNcoaWaiver)
            includedTerms.push(SignatureTerms.ncoaFulfillmentWaiverTerms);
        if (isDsf2)
            includedTerms.push(SignatureTerms.dsf2Terms);

        includedTerms.push(SignatureTerms.signatureDisclaimer);

        let terms: React.ReactElement = React.createElement(React.Fragment, undefined, includedTerms);
        setSignatureDialogProps({
            source: source,
            title: "USPS Processing Acknowledgement Form e-Signature",
            terms: terms,
        })
        setSignatureDialogOpen(true);
    }
    const handleSignatureDialog_Show = () => {
        signatureDialog_Show(source);
    }
    const setListOwnerSignature = (data?: IPafApplicationSignature) => {
        setValue("list_owner_signature_attest", data?.is_attest, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        setValue("list_owner_signature_text", data?.signature_text, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        setValue("list_owner_signature_date", data?.signature_date, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
    const clearListOwnerSignature = () => {
        setValue("list_owner_signature_attest", false, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
        setValue("list_owner_signature_text", "", { shouldDirty: true, shouldTouch: true, shouldValidate: false });
        setValue("list_owner_signature_date", null as any, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
    }
    const setListAdminSignature = (data?: IPafApplicationSignature) => {
        setValue("list_admin_signature_attest", data?.is_attest, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        setValue("list_admin_signature_text", data?.signature_text, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        setValue("list_admin_signature_date", data?.signature_date, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }

    const clearListAdminSignature = () => {
        setValue("list_admin_signature_attest", false, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
        setValue("list_admin_signature_text", "", { shouldDirty: true, shouldTouch: true, shouldValidate: false });
        setValue("list_admin_signature_date", null as any, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
    }

    const setLicenseeSignature = (data?: IPafApplicationSignature) => {
        const attestValue = data?.is_attest ?? false;
        setValue("licensee_signature_attest", attestValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        setValue("licensee_signature_text", data?.signature_text, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        setValue("licensee_signature_date", data?.signature_date, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }

    const clearLicenseeSignature = () => {
        setValue("licensee_signature_attest", false, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
        setValue("licensee_signature_text", "", { shouldDirty: true, shouldTouch: true, shouldValidate: false });
        setValue("licensee_signature_date", null as any, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
    }

    const signatureDialog_Close = (source: SignatureSourceType, action: string, data?: IPafApplicationSignature) => {
        if (source === "listOwner") {
            if (action === "submit") {
                // Set values on PAFApplication object prior to saving
                setListOwnerSignature(data);
            } else {
                clearListOwnerSignature();
            }
        }
        if (source === "listAdmin") {
            if (action === "submit") {
                // Set values on PAFApplication object prior to saving
                setListAdminSignature(data);
            } else {
                clearListAdminSignature();
            }
        }
        if (source === "licensee") {
            if (action === "submit") {
                setLicenseeSignature(data);
            } else {
                clearLicenseeSignature();
            }
        }
        setSignatureDialogOpen(false);
    }

    const clearSignature = () => {
        if (source === "listOwner") {
            clearListOwnerSignature();
        }
        if (source === "listAdmin") {
            clearListAdminSignature();
        }
        if (source === "licensee") {
            clearLicenseeSignature();
        }
    }
    return (
        <>
            <Grid container spacing={theme.spacing(1)}>
                <Grid item xs={12} md={12}>
                    <ButtonBar buttonAlignment="alignLeft">
                        <DaButton buttonStyle="primary" onClick={() => { handleSignatureDialog_Show() }}>
                            e-Sign Paf
                        </DaButton>
                        <DaButton buttonStyle="secondary" onClick={() => { clearSignature() }}>
                            Clear e-Signature
                        </DaButton>
                    </ButtonBar>
                </Grid>
            </Grid>
            <SignatureDialog
                isOpen={signatureDialogOpen}
                onClose={signatureDialog_Close}
                {...signatureDialogProps}
            />
        </>
    );
};